import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import {
  AXIS_META,
  AxisKey,
  CHART_DEFS,
  FILTER_DEFS,
  KPI_DEFS,
  MONTH_NUMS,
  RATING_SCALE,
  RatingValue,
  RESULT_METRIC_DEFS,
  SalesDataLinkageInputState,
} from "./types";
import { getAxisChartSeries, getAxisKpiList, getResultUnitLabel } from "./utils";
import { PlaceholderNote, RatingIcon, ScaleLegend, SearchIcon, SectionTitle, StepHeader } from "./Common";
import { WebAPIContext } from "../../../contexts/WebAPIContext";

interface Props {
  axisKey: AxisKey;
  state: SalesDataLinkageInputState;
  isEditable: boolean;
  namespace: string;
  onChange: (patch: Partial<SalesDataLinkageInputState>) => void;
  /** 営業データ連携の検索クエリに渡すプロジェクトID */
  projectId?: string;
}

const AxisStep: React.FC<Props> = ({ axisKey, state, isEditable, namespace, onChange, projectId }) => {
  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);
  /** 検索中フラグ（BigQuery連携API呼び出し中はボタンを非活性化） */
  const [isSearching, setIsSearching] = useState(false);
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>): string =>
    t(`features.InvestmentScreening.${namespace}.salesDataLinkage.${key}`, options as any) as unknown as string;

  const meta = AXIS_META[axisKey];
  const included = state.axisInclude[axisKey];
  const searched = !!state.searched[axisKey];
  const currency = state.displayCurrency;
  const currentEval = state.axisEvals[axisKey];

  const resultMetric = state.resultMetric;

  const kpi = useMemo(() => getAxisKpiList(axisKey, currency), [axisKey, currency]);
  const series = useMemo(
    () => getAxisChartSeries(axisKey, currency, resultMetric),
    [axisKey, currency, resultMetric]
  );

  const handleFilterChange = (code: string, value: string) => {
    onChange({ filterValues: { ...state.filterValues, [code]: value } });
  };

  const toast = useToast();

  const handleSearch = async () => {
    if (isSearching) return;
    setIsSearching(true);
    try {
      // 営業データ連携情報の取得（BigQuery連携API）
      const functionName = "getSalesDataLinkageByProjectId";
      const response = await WebAPI.request({
        query: `
          query ${functionName}($projectId: String) {
            ${functionName}(projectId: $projectId) {
              kpi {
                ytdActual
                landing
                prevLanding
                confirmedExpect
                unconfirmedExpect
              }
              monthly {
                amount { actual plan expectConfirmed expectUnconfirmed }
                unitPrice { actual plan expectConfirmed expectUnconfirmed }
                quantity { actual plan expectConfirmed expectUnconfirmed }
              }
              connectivityCheckOk
              connectivityCheckMessage
            }
          }`,
        variables: { projectId },
      });
      const result = response?.data?.[functionName];
      // BigQuery疎通確認結果をトーストで明示する（TODO: 本テーブル実装後、実データ検索結果の
      // 表示に置き換える際にこのトースト表示は不要になる想定）
      if (result?.connectivityCheckOk) {
        toast({
          description: result.connectivityCheckMessage ?? "BigQueryへの疎通確認に成功しました。",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          description: result?.connectivityCheckMessage ?? "BigQueryへの疎通確認に失敗しました。",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
      onChange({ searched: { ...state.searched, [axisKey]: true } });
    } catch (error) {
      console.error("getSalesDataLinkageByProjectId error:", error);
      toast({
        description: "営業データ連携情報の取得に失敗しました。",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSearching(false);
    }
  };

  const startYear = Number(state.startYm.split("-")[0]) || new Date().getFullYear();
  const monthLabels = MONTH_NUMS.map((m) => {
    const y = m >= 4 ? startYear : startYear + 1;
    return `${y}/${String(m).padStart(2, "0")}`;
  });

  const actualChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: tr("chart.actual"),
        data: series.actual,
        backgroundColor: "#2f6bd4",
      },
    ],
  };
  const planChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: tr("chart.plan"),
        data: series.plan,
        backgroundColor: "#16a34a",
      },
    ],
  };
  const expectChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: tr("chart.expectConfirmed"),
        data: series.expectConfirmed,
        backgroundColor: "#7c5cff",
      },
      {
        label: tr("chart.expectUnconfirmed"),
        data: series.expectUnconfirmed,
        backgroundColor: "#c9bdff",
      },
    ],
  };
  const chartDataById: Record<string, typeof actualChartData> = {
    actual: actualChartData,
    plan: planChartData,
    expect: expectChartData,
  };
  const kpiValueMap: Record<string, { value: string; color?: string }> = {
    ytdActual: { value: kpi.ytdActual },
    landing: { value: kpi.landing },
    achieveRate: {
      value: `${kpi.achieveRate.toFixed(1)}%`,
      color: kpi.achieveRate >= 100 ? "#16a34a" : "#dc2626",
    },
    yoy: {
      value: `${kpi.yoy >= 0 ? "+" : ""}${kpi.yoy.toFixed(1)}%`,
      color: kpi.yoy >= 0 ? "#16a34a" : "#dc2626",
    },
    confirmedRate: { value: `${kpi.confirmedRate.toFixed(1)}%` },
  };

  return (
    <VStack align="stretch" spacing="16px">
      <SectionTitle
        title={tr("section.axisTitle", { axisLabel: tr(`tab.${meta.labelKey}`) })}
        note={tr("section.axisSubtitle")}
      />

      <Box bg="#fff" border="1px solid #e2e8f0" borderRadius="10px" boxShadow="0 1px 3px rgba(16,42,80,.06)" p="16px 18px">
        <StepHeader
          step={1}
          title={tr("label.axisStep1Title", { axisLabel: tr(`tab.${meta.labelKey}`) })}
          note={tr("label.axisStep1Note")}
        />
        <Box borderTop="1px solid #e2e8f0" my="12px" />
        <HStack spacing="10px">
          <Button
            size="sm"
            fontSize="12.5px"
            fontWeight={700}
            borderRadius="8px"
            isDisabled={!isEditable}
            bg={included ? "#eaf1fb" : "#fff"}
            color={included ? "#1e4fa0" : "#b0b8c4"}
            border="1px solid"
            borderColor={included ? "#2f6bd4" : "#e2e8f0"}
            _hover={{ bg: included ? "#eaf1fb" : "#f8fafc" }}
            onClick={() => onChange({ axisInclude: { ...state.axisInclude, [axisKey]: true } })}
          >
            {tr("toggle.include")}
          </Button>
          <Button
            size="sm"
            fontSize="12.5px"
            fontWeight={700}
            borderRadius="8px"
            isDisabled={!isEditable}
            bg={!included ? "#f1f5f9" : "#fff"}
            color={!included ? "#64748b" : "#b0b8c4"}
            border="1px solid"
            borderColor={!included ? "#cbd5e1" : "#e2e8f0"}
            _hover={{ bg: !included ? "#f1f5f9" : "#f8fafc" }}
            onClick={() => onChange({ axisInclude: { ...state.axisInclude, [axisKey]: false } })}
          >
            {tr("toggle.exclude")}
          </Button>
        </HStack>
      </Box>

      {!included && (
        <PlaceholderNote
          title={tr("label.excludedTitle")}
          desc={tr("label.excludedDesc")}
          bg="#f8fafc"
          p="56px 20px"
        />
      )}

      {included && (
        <>
          <Box bg="#fff" border="1px solid #e2e8f0" borderRadius="10px" boxShadow="0 1px 3px rgba(16,42,80,.06)" p="16px 18px">
            <StepHeader step={2} title={tr("label.searchStepTitle")} note={tr("label.searchStepNote")} />
            <Box borderTop="1px solid #e2e8f0" my="12px" />
            <Grid templateColumns="repeat(auto-fit, minmax(160px, 1fr))" gap="10px">
              <GridItem>
                <Text fontSize="11px" color="#94a3b8" mb="2px">
                  {tr("label.startYearMonth")} (SLYY/FIYY/SEYY)
                </Text>
                <input
                  type="month"
                  value={state.startYm}
                  disabled={!isEditable}
                  onChange={(e) => onChange({ startYm: e.target.value })}
                  style={{ width: "100%", padding: "6px 10px", border: "1px solid #cbd5e0", borderRadius: "6px" }}
                />
              </GridItem>
              <GridItem>
                <Text fontSize="11px" color="#94a3b8" mb="2px">
                  {tr("label.endYearMonth")} (SLYY/FIYY/SEYY)
                </Text>
                <input
                  type="month"
                  value={state.endYm}
                  disabled={!isEditable}
                  onChange={(e) => onChange({ endYm: e.target.value })}
                  style={{ width: "100%", padding: "6px 10px", border: "1px solid #cbd5e0", borderRadius: "6px" }}
                />
              </GridItem>
              {FILTER_DEFS.map((f) => (
                <GridItem key={f.code}>
                  <Text fontSize="11px" color="#94a3b8" mb="2px">
                    {tr(`filter.${f.labelKey}`)} ({f.code})
                  </Text>
                  <Select
                    size="sm"
                    isDisabled={!isEditable}
                    value={state.filterValues[f.code] ?? f.options[0]}
                    onChange={(e) => handleFilterChange(f.code, e.target.value)}
                  >
                    {f.options.map((o) => (
                      <option key={o} value={o}>
                        {o === "全て" ? tr("common.all") : o}
                      </option>
                    ))}
                  </Select>
                </GridItem>
              ))}
            </Grid>
            <Flex justify="flex-end" mt="16px">
              <Button
                h="auto"
                px="22px"
                py="9px"
                bg="#2f6bd4"
                color="#fff"
                borderRadius="7px"
                fontSize="13px"
                fontWeight={700}
                _hover={{ bg: "#1e4fa0" }}
                leftIcon={<SearchIcon />}
                isDisabled={!isEditable || isSearching}
                isLoading={isSearching}
                onClick={handleSearch}
              >
                {tr("button.search")}
              </Button>
            </Flex>
          </Box>

          {!searched && (
            <PlaceholderNote
              icon={<SearchIcon />}
              title={tr("label.notSearchedTitle")}
              desc={tr("label.notSearchedDesc")}
            />
          )}

          {searched && (
            <>
              <Box bg="#fff" border="1px solid #e2e8f0" borderRadius="10px" boxShadow="0 1px 3px rgba(16,42,80,.06)" p="16px 18px">
              <StepHeader step={3} title={tr("label.resultStepTitle")} note={tr("label.resultStepNote")} />
              <Box borderTop="1px solid #e2e8f0" my="12px" />

              <HStack spacing="6px" mb="14px">
                {RESULT_METRIC_DEFS.map((rm) => {
                  const active = resultMetric === rm.key;
                  return (
                    <Button
                      key={rm.key}
                      size="sm"
                      fontSize="12.5px"
                      fontWeight={700}
                      borderRadius="8px"
                      isDisabled={!isEditable}
                      bg={active ? "#eaf1fb" : "#fff"}
                      color={active ? "#1e4fa0" : "#64748b"}
                      border="1px solid"
                      borderColor={active ? "#2f6bd4" : "#e2e8f0"}
                      _hover={{ bg: active ? "#eaf1fb" : "#f8fafc" }}
                      onClick={() => onChange({ resultMetric: rm.key })}
                    >
                      {tr(`resultTab.${rm.labelKey}`)}
                    </Button>
                  );
                })}
              </HStack>

              <Grid templateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap="12px" mb="16px">
                {KPI_DEFS.map((kd) => {
                  const kv = kpiValueMap[kd.key];
                  return (
                    <GridItem
                      key={kd.key}
                      bg="#fff"
                      border="1px solid #e2e8f0"
                      borderTop={`3px solid ${kd.accent}`}
                      borderRadius="10px"
                      p="12px"
                      boxShadow="0 1px 3px rgba(16,42,80,.06)"
                    >
                      <Text fontSize="11px" color="#64748b" mb="4px">
                        {tr(`kpi.${kd.labelKey}`)}
                      </Text>
                      <Text fontSize="18px" fontWeight={700} color={kv.color ?? "#16233f"}>
                        {kv.value}
                      </Text>
                      <Text fontSize="10px" color="#9aa0a6" mt="4px">
                        {tr(`kpi.${kd.noteKey}`)}
                      </Text>
                    </GridItem>
                  );
                })}
              </Grid>

              <Grid templateColumns="repeat(auto-fit, minmax(280px, 1fr))" gap="12px">
                {CHART_DEFS.map((cd) => (
                  <Box key={cd.id} bg="#fff" border="1px solid #e2e8f0" borderRadius="10px" p="12px">
                    <Flex justify="space-between" align="center" mb="2px">
                      <HStack spacing="6px">
                        <Box w="10px" h="10px" borderRadius="2px" bg={cd.tagColor} />
                        <Text fontSize="13px" fontWeight={700} color="#16233f">
                          {tr("chart.titleWithMetric", {
                            title: tr(`chart.${cd.titleKey}`),
                            metric: tr(`resultTab.${resultMetric}`),
                          })}
                        </Text>
                      </HStack>
                      <Text fontSize="11px" color="#64748b" whiteSpace="nowrap">
                        {tr("label.unit")}：{getResultUnitLabel(resultMetric, currency)}
                      </Text>
                    </Flex>
                    <Text fontSize="10px" color="#9aa0a6" mb="8px">
                      {tr(`chart.${cd.sourceKey}`)}
                    </Text>
                    <HStack spacing="10px" mb="6px" minH="16px">
                      {chartDataById[cd.id].datasets.length > 1 &&
                        chartDataById[cd.id].datasets.map((ds) => (
                          <HStack key={ds.label} spacing="4px">
                            <Box w="8px" h="8px" borderRadius="2px" bg={ds.backgroundColor} />
                            <Text fontSize="10px" color="#5f6b7a">
                              {ds.label}
                            </Text>
                          </HStack>
                        ))}
                    </HStack>
                    <Box height="220px">
                      <Bar
                        data={chartDataById[cd.id]}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { display: false },
                            datalabels: { display: false },
                          },
                          scales: {
                            x: {
                              stacked: cd.id === "expect",
                              grid: { display: true, drawOnChartArea: true, drawTicks: true },
                            },
                            y: {
                              stacked: cd.id === "expect",
                              grid: { display: false, drawOnChartArea: false, drawTicks: false },
                              ticks: { display: false },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Grid>
              </Box>

              <Box bg="#fff" border="1px solid #e2e8f0" borderRadius="10px" boxShadow="0 1px 3px rgba(16,42,80,.06)" p="16px 18px">
                <StepHeader
                  step={4}
                  title={tr("label.evalStepTitle", {
                    axisLabel: tr(`tab.${meta.labelKey}`),
                    aspect: tr(`axisMeta.${meta.key}.aspect`),
                  })}
                  note={tr(`axisMeta.${meta.key}.guide`)}
                />
                <Box borderTop="1px solid #e2e8f0" my="12px" />
                <ScaleLegend namespace={namespace} mb="16px" />
                <HStack spacing="8px" flexWrap="wrap">
                  {RATING_SCALE.map((r) => {
                    const selected = currentEval === r.val;
                    return (
                      <Button
                        key={r.val}
                        title={tr(`ratingScale.${r.labelKey}`)}
                        aria-label={tr(`ratingScale.${r.labelKey}`)}
                        w="46px"
                        h="46px"
                        minW="46px"
                        p={0}
                        fontSize="21px"
                        fontWeight={700}
                        borderRadius="11px"
                        border="1.5px solid"
                        isDisabled={!isEditable}
                        borderColor={selected ? r.color : "#e2e8f0"}
                        color={selected ? "#fff" : "#b0b8c4"}
                        bg={selected ? r.color : "#fff"}
                        _hover={{ bg: selected ? r.color : "#f8fafc" }}
                        onClick={() => onChange({ axisEvals: { ...state.axisEvals, [axisKey]: r.val as RatingValue } })}
                      >
                        <RatingIcon val={r.val} size={20} />
                      </Button>
                    );
                  })}
                </HStack>
                <Box mt="16px">
                  <Text fontSize="13px" fontWeight={700} color="#16233f" mb="6px">
                    {tr("label.qualitativeNoteLabel")}
                  </Text>
                  <Textarea
                    value={state.axisNotes[axisKey] ?? ""}
                    onChange={(e) =>
                      onChange({ axisNotes: { ...state.axisNotes, [axisKey]: e.target.value } })
                    }
                    isDisabled={!isEditable}
                    placeholder={tr("label.qualitativeNotePlaceholder")}
                    rows={6}
                    fontSize="13px"
                    bg="#fff"
                    borderColor="#e2e8f0"
                    borderRadius="8px"
                  />
                </Box>
              </Box>
            </>
          )}
        </>
      )}
    </VStack>
  );
};

export default AxisStep;
